import { useCallback } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";

import {
  CreateWorkflowDocument,
  type CreateWorkflowInput,
  DeleteWorkflowDocument,
  UpdateWorkflowDocument,
  type UpdateWorkflowInput,
  ValidateWorkflowDeletionDocument,
  ValidateWorkflowKeyDocument,
  WorkflowsListDataDocument,
} from "#app/lib/data";
import {
  insertIntoConnection,
  removeFromConnection,
} from "#app/lib/data/connection-cache-utils";
import { useAnalytics, AnalyticsContext, SettingsEvent } from "#app/lib/analytics";

export function useWorkflowMutations() {
  const client = useApolloClient();
  const { track } = useAnalytics();

  const [createWorkflowMutation] = useMutation(CreateWorkflowDocument, {
    update(cache, { data }) {
      const result = data?.createWorkflow;
      if (
        result &&
        result.__typename !== "ValidationError" &&
        result.__typename !== "PermissionDenied" &&
        "workflowId" in result
      ) {
        insertIntoConnection(cache, "workflows", "WorkflowEdge", {
          __typename: "Workflow",
          workflowId: result.workflowId,
        });
      }
    },
  });

  const [updateWorkflowMutation] = useMutation(UpdateWorkflowDocument);

  const [deleteWorkflowMutation] = useMutation(DeleteWorkflowDocument, {
    update(cache, { data }) {
      const result = data?.deleteWorkflow;
      if (result && "workflowId" in result) {
        removeFromConnection(
          cache,
          "workflows",
          { __typename: "Workflow", workflowId: result.workflowId },
          { evict: true },
        );
      }
    },
  });

  const createWorkflow = useCallback(
    async (input: CreateWorkflowInput) => {
      const { data } = await createWorkflowMutation({
        variables: { input },
      });
      if (data?.createWorkflow.__typename === "Workflow") {
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Create, label: "workflow" });
      }
      return data?.createWorkflow ?? null;
    },
    [createWorkflowMutation, track],
  );

  const updateWorkflow = useCallback(
    async (workflowId: string, input: UpdateWorkflowInput) => {
      const { data } = await updateWorkflowMutation({
        variables: { id: workflowId, input },
      });
      if (data?.updateWorkflow.__typename === "Workflow") {
        // Archive and restore ride this update; name them apart from edits.
        const event =
          input.archived === true
            ? SettingsEvent.Archive
            : input.archived === false
              ? SettingsEvent.Unarchive
              : SettingsEvent.Update;
        track({
          context: AnalyticsContext.Settings,
          event,
          label: "workflow",
          data: { fields: Object.keys(input) },
        });
      }
      return data?.updateWorkflow ?? null;
    },
    [updateWorkflowMutation, track],
  );

  const deleteWorkflow = useCallback(
    async (workflowId: string) => {
      const { data } = await deleteWorkflowMutation({
        variables: { id: workflowId },
      });
      if (data?.deleteWorkflow.__typename === "Workflow") {
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Delete, label: "workflow" });
      }
      return data?.deleteWorkflow ?? null;
    },
    [deleteWorkflowMutation, track],
  );

  // Promotion rides the ordinary update. The API also keeps a dedicated
  // `setDefaultWorkflow` mutation for consumers outside this repo (the
  // schema is additive-only, so it can never go) — the app speaks through
  // one door so there is one place to change. The refetch is still needed
  // either way: promotion clears the flag on whichever workflow held it,
  // and only the promoted row comes back in the response.
  const setDefaultWorkflow = useCallback(
    async (workflowId: string) => {
      const { data } = await updateWorkflowMutation({
        variables: { id: workflowId, input: { isDefault: true } },
      });
      if (data?.updateWorkflow.__typename === "Workflow") {
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.SetDefault, label: "workflow" });
      }
      await client.refetchQueries({ include: [WorkflowsListDataDocument] });
      // The union as the server sent it: a refusal carries its reason.
      return data?.updateWorkflow ?? null;
    },
    [updateWorkflowMutation, client, track],
  );

  const validateWorkflowDeletion = useCallback(
    async (workflowId: string) => {
      const { data, error } = await client.query({
        query: ValidateWorkflowDeletionDocument,
        variables: { id: workflowId },
        fetchPolicy: "network-only",
      });
      // errorPolicy "all" resolves with `error` set instead of rejecting —
      // rethrow so the caller's catch path stays the failure path.
      if (!data) throw error ?? new Error("Failed to validate workflow deletion");
      return data.validateWorkflowDeletion;
    },
    [client],
  );

  const validateWorkflowKey = useCallback(
    async (key: string) => {
      const { data, error } = await client.query({
        query: ValidateWorkflowKeyDocument,
        variables: { key },
        fetchPolicy: "network-only",
      });
      // errorPolicy "all" resolves with `error` set instead of rejecting —
      // rethrow so the caller's catch path stays the failure path.
      if (!data) throw error ?? new Error("Failed to validate workflow key");
      return data.validateWorkflowKey;
    },
    [client],
  );

  return {
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,
    setDefaultWorkflow,
    validateWorkflowDeletion,
    validateWorkflowKey,
  };
}
